import { IIdentityProviderResponse } from '@kcftech/prometheustypescriptapiclient';
import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DocumentTitle } from 'components/DocumentTitle';
import { TablePageLayout } from 'components/page/styled';
import { usePagedIdentityProvidersDataSource } from 'custom_hooks/data_hooks/data_source/IdentityProvidersDataSource';
import { AddIdentityProviderModal } from 'pages/Settings/IdentityProvidersPage/AddIdentityProviderModal';
import { IdentityProviderNameCell } from 'pages/Settings/IdentityProvidersPage/IdentityProviderNameCell';
import { IdentityProviderUsernameCell } from 'pages/Settings/IdentityProvidersPage/IdentityProviderUsernameCell';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { useEffect, useMemo, useState } from 'react';
import { getId } from 'utils/data';

export type IdentityProvidersTableProps = Readonly<{
    rowData: IIdentityProviderResponse;
    onChange: () => void;
}>;

const identityProviderTableQueryName = 'identity-providers-table';

export function IdentityProviders() {
    const dataSource = usePagedIdentityProvidersDataSource({
        name: identityProviderTableQueryName,
        defaultFilter: {
            nameSearch: undefined,
        },
        defaultSort: [
            {
                field: 'CorporationName',
                dir: 'asc',
            },
        ],
    });
    const { setFilter, reloadPage, reset } = dataSource;

    const [nameSearch, setNameSearch] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);

    useEffect(() => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            nameSearch,
        }));
    }, [nameSearch, setFilter]);

    const onIdentityProviderCreate = () => {
        setAddModalOpen(false);
        reset();
        reloadPage();
    };

    const columns = useMemo<TableColumn<IIdentityProviderResponse>[]>(
        () => [
            {
                id: 'Corporation',
                header: () => 'Corporation',
                value: ({ rowData }) => `${rowData.CorporationChild.CorporationName}`,
                sortOn: ['CorporationName'],
                locked: true,
                primary: true,
            },
            {
                id: 'IdentityProviderName',
                header: () => 'Identity Provider Name',
                value: ({ rowData }) => (
                    <IdentityProviderNameCell rowData={rowData} onChange={reloadPage} />
                ),
                sortOn: ['Name'],
                primary: true,
            },
            {
                id: 'UsernameIdentifiers',
                header: () =>
                    'Username Identifiers (Separate multiple identifiers with a comma)',
                value: ({ rowData }) => (
                    <IdentityProviderUsernameCell
                        rowData={rowData}
                        onChange={reloadPage}
                    />
                ),
                primary: true,
            },
        ],
        [reloadPage]
    );

    return (
        <TablePageLayout>
            <DocumentTitle title="Identity Provider Settings" />

            <SearchCreateButtonContainer>
                <DebouncedSearchInput
                    placeholder="Search"
                    value={nameSearch}
                    onChange={setNameSearch}
                />

                <div className="flex-grow-1" />
                <CreateButton onClick={() => setAddModalOpen(true)} />
                {addModalOpen && (
                    <AddIdentityProviderModal
                        onCreate={onIdentityProviderCreate}
                        onClose={() => setAddModalOpen(false)}
                    />
                )}
            </SearchCreateButtonContainer>
            <DataSourceTable
                noResultMessage="No identity providers found."
                rowKey={getId}
                columns={columns}
                dataSource={dataSource}
            />
        </TablePageLayout>
    );
}
